import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

const MultiSelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const MultiSelectBox = styled.div`
  width: 100%;
  padding: 0.5rem 0.6rem;
  margin: 0.4rem 0;
  background: #ffffff;
  // border: 1px solid rgba(0, 102, 255, 0.15);
    border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 1rem;
  border: none;
  border-bottom: 1px solid rgba(0, 102, 255, 0.15);
  font-size: 0.9rem;
  outline: none;
  
  &:focus {
    border-bottom-color: var(--primary);
  }
`;

const Dropdown = styled.div`
  position: absolute;
  width: 100%;
  background: white;
  border: 1px solid rgba(0, 102, 255, 0.15);
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.05);
  z-index: 100;
  max-height: 250px;
  overflow-y: auto;
  margin-top: 4px;
`;

const Option = styled.label`
  display: flex;
  align-items: flex-start;
  padding: 0.6rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  gap: 0.5rem;
  &:hover {
    background: #f5f5f5;
  }

  input {
    margin-top: 0.2rem;
  }

  .details {
    display: flex;
    flex-direction: column;
  }

  .label {
    font-weight: 500;
  }

  .desc {
    font-size: 0.8rem;
    color: #666;
  }
`;

const SelectedTag = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(0, 102, 255, 0.1);
  // color: var(--primary);
  padding: 0.2rem 0.5rem;
  margin: 0.1rem;
  border-radius: 4px;
  font-size: 0.8rem;
  
  button {
    background: none;
    border: none;
    // color: var(--primary);
    margin-left: 0.3rem;
    cursor: pointer;
    font-size: 0.9rem;
    
    &:hover {
      color: #ff4444;
    }
  }
`;

const SelectedContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex: 1;
  min-height: 1.2rem;
`;

const Caret = styled.span`
  font-size: 0.8rem;
  margin-left: 0.5rem;
`;

const NoResults = styled.div`
  padding: 0.8rem 1rem;
  color: #666;
  font-size: 0.9rem;
  text-align: center;
`;

const TruncatedText = styled.div`
  max-width: 300px;      
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const MultiSelectDropdownSearch = ({
  options = [],
  selected = [],
  onChange,
  placeholder = 'Select options...',
  searchPlaceholder = 'Search options...',
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const getOptionValue = (option) => option?.name;
  const getOptionLabel = (option) => option?.name;
 
  const filteredOptions = options.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpen(false);
      setSearchTerm('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option) => {
    const isSelected = selected.some(
      (sel) => getOptionValue(sel) === getOptionValue(option)
    );

    let newSelection;
    if (isSelected) {
      newSelection = selected.filter(
        (sel) => getOptionValue(sel) !== getOptionValue(option)
      );
    } else {
      newSelection = [...selected, option];
    }

    onChange(newSelection);
  };

  const removeOption = (optionToRemove) => {
    const newSelection = selected.filter(
      (sel) => getOptionValue(sel) !== getOptionValue(optionToRemove)
    );
    onChange(newSelection);
  };

  const isOptionSelected = (option) => {
    return selected.some(
      (sel) => getOptionValue(sel) === getOptionValue(option)
    );
  };

  const handleBoxClick = () => {
    setOpen(!open);
    if (!open) {
      setTimeout(() => {
        const searchInput = dropdownRef.current?.querySelector(
          'input[type="text"]'
        );
        searchInput?.focus();
      }, 100);
    }
  };

  return (
    <MultiSelectWrapper ref={dropdownRef}>
      <MultiSelectBox onClick={handleBoxClick}>
        <SelectedContainer>
          {selected.length === 0 ? (
            <span style={{ color: '#999' }}>{placeholder}</span>
          ) : (
            selected.map((selectedOption, index) => (
              <SelectedTag key={index}>
                {getOptionLabel(selectedOption)}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOption(selectedOption);
                  }}
                >
                  ×
                </button>
              </SelectedTag>
            ))
          )}
        </SelectedContainer>
        <Caret>▾</Caret>
      </MultiSelectBox>

      {open && (
        <Dropdown>
          <SearchInput
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />

          {filteredOptions.length === 0 ? (
            <NoResults>No options found</NoResults>
          ) : (
            filteredOptions.map((option, index) => (
              <Option key={index}>
                <input
                  type="checkbox"
                  checked={isOptionSelected(option)}
                  onChange={() => toggleOption(option)}
                />
                <div className="details">
                  <span className="label">{option.name.length > 50 ? option.name.substring(0, 50) + '...' : option.name}</span>
                  {(option.description || option?.desc) && (
                    <span className="desc">
                      <TruncatedText title={option.description || option?.desc}>
                        {option.description && option.description.length > 50 ? option.description.substring(0, 50) + '...': option.description}
                        {option?.desc && option?.desc.length > 50 ? option?.desc.substring(0, 50) + '...': option?.desc}
                      </TruncatedText>
                      </span>
                  )}
                </div>
              </Option>
            ))
          )}
        </Dropdown>
      )}
    </MultiSelectWrapper>
  );
};
